import type { AxialCoord } from "./hex";

/**
 * The symmetry groups of the hex lattice about the map centre that the starts are drawn in (K5,
 * ADR-0017 point 5, docs/design/KINGDOM-SEATS-IDEA.md section 7.1). The lattice's turns and
 * mirrors about a hex make groups of order 1, 2, 3, 4, 6 and 12 only; these are the ones used:
 * C1 (none: the spread rule alone), C2 (the duel's half turn), C3 (120 degrees), D2 (the half
 * turn and a mirror), C6 (60 degrees) and D6 (every turn and mirror). A group's order is the
 * number of sides its starts are images for. Exact integer arithmetic only, so every engine agrees.
 */
export type SymmetryName = "C1" | "C2" | "C3" | "D2" | "C6" | "D6";

interface SymmetryElement {
  /** Sixth turns counter-clockwise, applied after the mirror. */
  readonly turns: number;
  readonly mirrored: boolean;
}

const SIXTHS = 6;

function turnsOf(...turns: readonly number[]): SymmetryElement[] {
  return turns.map((count) => ({ turns: count, mirrored: false }));
}

function mirrorsOf(...turns: readonly number[]): SymmetryElement[] {
  return turns.map((count) => ({ turns: count, mirrored: true }));
}

/** Each group's elements, the identity first: element j carries side 1's starts onto side j + 1's. */
const ELEMENTS: Readonly<Record<SymmetryName, readonly SymmetryElement[]>> = {
  C1: turnsOf(0),
  C2: turnsOf(0, 3),
  C3: turnsOf(0, 2, 4),
  D2: [...turnsOf(0, 3), ...mirrorsOf(0, 3)],
  C6: turnsOf(0, 1, 2, 3, 4, 5),
  D6: [...turnsOf(0, 1, 2, 3, 4, 5), ...mirrorsOf(0, 1, 2, 3, 4, 5)],
};

/** The orders a free-for-all may use, largest first; a count none divides is C1's. */
const FREE_FOR_ALL: readonly SymmetryName[] = ["D6", "C6", "D2", "C3", "C2"];

/**
 * A sixth turn counter-clockwise about the centre: (q, r) -> (-r, q + r). Written as a difference
 * from 0 so no image is a negative zero.
 */
function turnOnce(coord: AxialCoord): AxialCoord {
  return { q: 0 - coord.r, r: coord.q + coord.r };
}

/** The mirror (q, r) -> (r, q), its axis at 30 degrees, between E and SE's neighbours. */
function mirror(coord: AxialCoord): AxialCoord {
  return { q: coord.r, r: coord.q };
}

function apply(element: SymmetryElement, coord: AxialCoord): AxialCoord {
  let image = element.mirrored ? mirror(coord) : coord;
  for (let turn = 0; turn < element.turns; turn += 1) {
    image = turnOnce(image);
  }
  return image;
}

/** How many elements the group has: the sides its starts are images for. */
export function symmetryOrder(symmetry: SymmetryName): number {
  return ELEMENTS[symmetry].length;
}

/**
 * The size of the smallest orbit off the centre: the turns of the group. Every orbit's size is a
 * multiple of it, so a symmetric map holds 1 plus a multiple of it hexes.
 */
export function symmetryStep(symmetry: SymmetryName): number {
  return ELEMENTS[symmetry].filter((element) => !element.mirrored).length;
}

/** `coord` under every element of the group, in element order; a hex an element fixes repeats. */
export function imagesOf(coord: AxialCoord, symmetry: SymmetryName): AxialCoord[] {
  return ELEMENTS[symmetry].map((element) => apply(element, coord));
}

/** The distinct images of `coord`, in element order, `coord` first. */
export function orbitOf(coord: AxialCoord, symmetry: SymmetryName): AxialCoord[] {
  const orbit: AxialCoord[] = [];
  for (const image of imagesOf(coord, symmetry)) {
    if (!orbit.some((seen) => seen.q === image.q && seen.r === image.r)) {
      orbit.push(image);
    }
  }
  return orbit;
}

/**
 * The group a free-for-all of `kingdoms` is drawn in: the largest order of 12, 6, 4, 3 and 2 that
 * divides it, with kingdoms / order kingdoms a side; C1 (the spread rule alone) for a count none
 * divides.
 */
export function freeForAllSymmetry(kingdoms: number): SymmetryName {
  return FREE_FOR_ALL.find((symmetry) => kingdoms % symmetryOrder(symmetry) === 0) ?? "C1";
}

/**
 * Where a hex lies round the centre, exactly: its ring and its step along the ring, counted
 * counter-clockwise from the ring's hex due E, in [0, 6 x ring). A sixth turn adds the ring to the
 * step; the mirror takes step s to ring - s. The centre is ring 0, step 0.
 */
function bearingOf(coord: AxialCoord): { readonly ring: number; readonly step: number } {
  const ring = (Math.abs(coord.q) + Math.abs(coord.r) + Math.abs(coord.q + coord.r)) / 2;
  if (ring === 0) {
    return { ring: 0, step: 0 };
  }
  // Turned back a sixth at a time until it lies in the first wedge, q >= 1 and r >= 0.
  let wedge = 0;
  let turned = coord;
  while (!(turned.q >= 1 && turned.r >= 0)) {
    turned = { q: turned.q + turned.r, r: 0 - turned.q };
    wedge += 1;
  }
  return { ring, step: wedge * ring + turned.r };
}

/**
 * Whether `coord` lies in side 1's sector: the wedge the group's images tile, measured from the
 * ring's E hex for a group of turns and from the mirror's axis for a mirrored one, one over the
 * group's order of a full turn wide, closed at its start. A hex no element but the identity fixes
 * has exactly one image in it. Every hex is in C1's.
 */
export function inSector(coord: AxialCoord, symmetry: SymmetryName): boolean {
  const order = symmetryOrder(symmetry);
  if (order === 1) {
    return true;
  }
  const { ring, step } = bearingOf(coord);
  if (ring === 0) {
    return false;
  }
  // A full turn in units the widths of all six orders divide: 12 a step of the ring.
  const unitsPerTurn = 12 * SIXTHS * ring;
  const mirrored = ELEMENTS[symmetry].some((element) => element.mirrored);
  // The mirror's axis lies a twelfth of a turn on, half way along the first wedge.
  const start = mirrored ? unitsPerTurn / 12 : 0;
  const offset = (((12 * step - start) % unitsPerTurn) + unitsPerTurn) % unitsPerTurn;
  return offset < unitsPerTurn / order;
}
